import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/compat/router';
import _ from 'lodash';
import { useObserver } from 'mobx-react-lite';

import { ARIA_ROLES } from 'shared/constants';
import { VerticalDivider } from 'components/core';
import { DesktopOnly, MobileOnly } from 'shared/components';
import ROUTES from 'src/routes';
import useUI from 'hooks/use-ui';

import useColors from 'hooks/use-colors';
import useUser from 'hooks/use-user';
import useSubscriptions from 'hooks/use-subscriptions';
import useOrdersForConsumer, { queryMoreOrders } from 'hooks/use-orders-for-consumer';

import MyAccount from 'components/forms/my-account';
import AccountSideNavBar from './account-side-nav-bar';
import AccountTopNavBar from './account-top-nav-bar';

import Preferences from './preferences';
import OrderList from './order-list';
import { BankAccount } from './bank-account';

const AccountPage = (): JSX.Element => {
  const router = useRouter();
  const UI = useUI();
  const { route } = router ?? {};
  const selectedTab = route === ROUTES.ORDER ? ROUTES.ORDERS : route;
  const colors = useColors();
  const user = useUser();
  const isLoggedIn = useObserver(() => user.exists);
  const loadingUser = useObserver(() => user.loading);
  const { subscriptions, unsubscribeToDispensaries } = useSubscriptions();
  const { orders, loading, fetchMore, totalCount } = useOrdersForConsumer();

  const handleLoadMoreOrders = (): void => {
    queryMoreOrders(fetchMore, orders);
  };

  useEffect(() => {
    if (!_.isBoolean(loadingUser) || user.token) {
      return;
    }
    const userNotLoggedIn = !isLoggedIn && !loadingUser;
    if (userNotLoggedIn) {
      UI.showHome();
    }
  }, [user, router, isLoggedIn, loadingUser, UI]);

  const renderTabs = (): JSX.Element => (
    <>
      {route === ROUTES.BANK_ACCOUNT && (
        <TabContainer
          id='account-tab-panel-bank-account'
          aria-labelledby='account-tab-area-bank-account'
          role={ARIA_ROLES.TAB_PANEL}
        >
          <BankAccount />
        </TabContainer>
      )}

      {route === ROUTES.ACCOUNT && (
        <TabContainer
          id='account-tab-panel-my-account'
          data-testid='account-tab-panel-my-account'
          aria-labelledby='account-tab-area-my-account'
          role={ARIA_ROLES.TAB_PANEL}
        >
          <MyAccount user={user} />
        </TabContainer>
      )}

      {route === ROUTES.PREFERENCES && (
        <TabContainer
          id='account-tab-panel-preferences'
          data-testid='account-tab-panel-preferences'
          aria-labelledby='account-tab-area-preferences'
          role={ARIA_ROLES.TAB_PANEL}
        >
          <Preferences
            subscriptions={subscriptions}
            // TODO(ECOMM-8582): Remove after converting Preferences to TypeScript
            // @ts-expect-error Preferences is JSX — its prop type is inferred as null from the default value
            unsubscribeToDispensaries={unsubscribeToDispensaries}
            user={user}
          />
        </TabContainer>
      )}

      {_.includes([ROUTES.ORDER, ROUTES.ORDERS], route) && (
        <TabContainer
          id='account-tab-panel-orders'
          data-testid='account-tab-panel-orders'
          aria-labelledby='account-tab-area-orders'
          role={ARIA_ROLES.TAB_PANEL}
        >
          <OrderList
            handleLoadMore={handleLoadMoreOrders}
            hasMoreOrders={orders.length < totalCount}
            loading={loading}
            orders={orders}
            user={user}
          />
        </TabContainer>
      )}
    </>
  );

  const renderDesktopOnly = (): JSX.Element => (
    <>
      <AccountSideNavBar selectedTab={selectedTab} user={user} />
      <VerticalDivider _color={colors.blueGrey[90]} />
      <MainContentContainer>{renderTabs()}</MainContentContainer>
    </>
  );

  const renderMobileOnly = (): JSX.Element => (
    <>
      <AccountTopNavBar selectedTab={selectedTab} />
      <MobileBackgroundContainer>{renderTabs()}</MobileBackgroundContainer>
    </>
  );

  return (
    <Wrapper id='main-content'>
      {isLoggedIn && <StyledMobileOnly mediaQuery='xs'>{renderMobileOnly()}</StyledMobileOnly>}
      {isLoggedIn && <StyledDesktopOnly mediaQuery='sm'>{renderDesktopOnly()}</StyledDesktopOnly>}
      {!isLoggedIn && (
        <StyledMobileOnly>
          <MobileBackgroundContainer />
        </StyledMobileOnly>
      )}
      {!isLoggedIn && (
        <StyledDesktopOnly>
          <MainContentContainer />
        </StyledDesktopOnly>
      )}
    </Wrapper>
  );
};

export default AccountPage;

const Wrapper = styled.main`
  width: 100%;
  display: flex;
`;

const StyledMobileOnly = styled(MobileOnly)`
  width: 100%;
  flex-direction: column;
`;
const StyledDesktopOnly = styled(DesktopOnly)`
  width: 100%;
`;

const MobileBackgroundContainer = styled.div`
  display: flex;
  justify-content: center;
  min-height: 100%;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.white};
  padding: 38px 25px 60px 25px;
`;

const MainContentContainer = styled.div`
  width: 100%;
  padding: 50px 79px 81px 82px;
  min-height: 616px !important;
`;

const TabContainer = styled.div`
  width: 100%;
`;
